/**
 * Casing + enum adapters between the marketing backend (snake_case) and
 * the frontend UI types (camelCase + lowercase enums).
 */

export function deepCamel<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) {
    return input.map((v) => deepCamel(v)) as unknown as T;
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      const camel = key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
      out[camel] = deepCamel(value);
    }
    return out as T;
  }
  return input as T;
}

export function deepSnake<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) {
    return input.map((v) => deepSnake(v)) as unknown as T;
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
      out[snake] = deepSnake(value);
    }
    return out as T;
  }
  return input as T;
}

export function enumLower<T extends string = string>(
  value: string | null | undefined,
  fallback: T,
): T {
  if (!value) return fallback;
  return value.toLowerCase().replace(/-/g, '_') as T;
}

export function enumUpper(value: string | null | undefined, fallback = ''): string {
  if (!value) return fallback;
  return value.toUpperCase().replace(/-/g, '_');
}

export function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value) : (value as number);
  return Number.isFinite(n) ? (n as number) : fallback;
}

export function centsToUsd(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function minutesAgoFromIso(iso: string | null | undefined): number {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  return Math.max(0, Math.round((Date.now() - then) / 60000));
}

export function relativeTimeFromIso(iso: string | null | undefined): string {
  const min = minutesAgoFromIso(iso);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

export function str(value: unknown, fallback = '—'): string {
  if (value == null || value === '') return fallback;
  return String(value);
}

/** Stable numeric id from opaque backend string ids (lead_*, comp_*, etc.). */
export function stableNumericId(id: string | number): number {
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h || 1;
}
